import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, Phone, Clock, CheckCircle, AlertCircle, Info, HelpCircle } from 'lucide-react';

export const Support: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const faqCategories = [
    {
      id: 'general',
      title: 'Algemene Vragen',
      icon: <HelpCircle className="w-5 h-5" />,
      questions: [
        {
          question: 'Wat is AI Geletterdheid?',
          answer: 'AI Geletterdheid is een training platform dat je helpt om kunstmatige intelligentie te begrijpen en te gebruiken. We bieden delen over AI-fundamentals, data & AI, en prompt engineering.'
        },
        {
          question: 'Hoe werkt de progress tracking?',
          answer: 'Je voortgang wordt automatisch bijgehouden wanneer je delen voltooit en quizzen afrondt. Je kunt je voortgang bekijken op de "Mijn Voortgang" pagina.'
        },
        {
          question: 'Kan ik de training op mijn eigen tempo volgen?',
          answer: 'Ja, je kunt de training volledig op je eigen tempo volgen. Er zijn geen tijdslimieten en je kunt altijd terugkeren naar eerdere delen.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technische Vragen',
      icon: <AlertCircle className="w-5 h-5" />,
      questions: [
        {
          question: 'De website laadt niet goed, wat moet ik doen?',
          answer: 'Probeer eerst je browser te verversen (F5 of Ctrl+R). Als het probleem aanhoudt, controleer je internetverbinding en probeer een andere browser.'
        },
        {
          question: 'Mijn voortgang wordt niet opgeslagen, wat nu?',
          answer: 'Zorg ervoor dat je cookies en local storage zijn ingeschakeld in je browser. Je voortgang wordt lokaal opgeslagen in je browser.'
        },
        {
          question: 'Kan ik de training op mobiel volgen?',
          answer: 'Ja, de website is responsive en werkt op alle apparaten. Voor de beste ervaring raden we een tablet of laptop aan.'
        }
      ]
    },
    {
      id: 'content',
      title: 'Inhoud & Delen',
      icon: <Info className="w-5 h-5" />,
      questions: [
        {
          question: 'Hoeveel delen zijn er beschikbaar?',
          answer: 'Momenteel hebben we 3 hoofddelen: AI Fundamentals (14 modules), Data & AI (3 modules), en ChatGPT & Prompt Engineering (1 module).'
        },
        {
          question: 'Komen er nieuwe delen bij?',
          answer: 'We werken continu aan uitbreiding van de content. Nieuwe delen en updates worden regelmatig toegevoegd.'
        },
        {
          question: 'Kan ik feedback geven op de content?',
          answer: 'Ja, we waarderen feedback! Je kunt contact met ons opnemen via de contactgegevens onderaan deze pagina.'
        }
      ]
    }
  ];

  const contactMethods = [
    {
      title: 'E-mail Support',
      description: 'Voor uitgebreide vragen en technische ondersteuning',
      icon: <Mail className="w-6 h-6" />,
      contact: 'support@vrules.nl',
      responseTime: 'Binnen 24 uur'
    },
    {
      title: 'Live Chat',
      description: 'Voor snelle vragen en directe hulp',
      icon: <MessageCircle className="w-6 h-6" />,
      contact: 'Beschikbaar tijdens kantooruren',
      responseTime: 'Direct'
    },
    {
      title: 'Telefoon',
      description: 'Voor urgente vragen en complexe problemen',
      icon: <Phone className="w-6 h-6" />,
      contact: '+31 (0) 123 456 789',
      responseTime: 'Binnen kantooruren'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Back button */}
      <Link 
        to="/"
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Terug naar home
      </Link>

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Support & Hulp</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          We helpen je graag verder! Hier vind je antwoorden op veelgestelde vragen en verschillende manieren om contact met ons op te nemen.
        </p>
      </div>

      {/* Contact Methods */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Opnemen</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {contactMethods.map((method, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mr-4">
                  {method.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{method.title}</h3>
              </div>
              <p className="text-gray-600 mb-3">{method.description}</p>
              <div className="space-y-2">
                <p className="font-medium text-gray-900">{method.contact}</p>
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{method.responseTime}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Veelgestelde Vragen</h2>
        
        {/* Category Selector */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setSelectedCategory('')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedCategory === '' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Alle Categorieën
          </button>
          {faqCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                selectedCategory === category.id 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="mr-2">{category.icon}</span>
              {category.title}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqCategories
            .filter(category => selectedCategory === '' || category.id === selectedCategory)
            .map((category) => (
              <div key={category.id} className="bg-white border border-gray-200 rounded-lg">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center">
                    <span className="mr-3">{category.icon}</span>
                    <h3 className="text-lg font-semibold text-gray-900">{category.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    {category.questions.map((faq, index) => (
                      <div key={index}>
                        <h4 className="font-medium text-gray-900 mb-2">{faq.question}</h4>
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* Quick Help */}
      <section className="bg-blue-50 rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Snelle Hulp</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Probleem Opgelost?</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Als je vraag is beantwoord, kun je direct verder met je AI-geletterdheid training.
            </p>
            <Link
              to="/topics"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Verder met Training
            </Link>
          </div>
          
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-orange-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Probleem Niet Opgelost?</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Neem contact met ons op via e-mail of telefoon voor persoonlijke ondersteuning.
            </p>
            <a
              href="mailto:support@vrules.nl"
              className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Contact Opnemen
            </a>
          </div>
        </div>
      </section>

      {/* Office Hours */}
      <section className="mt-8 text-center">
        <div className="bg-gray-100 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Kantooruren</h3>
          <p className="text-gray-600">
            Maandag t/m Vrijdag: 09:00 - 17:00<br />
            Weekend: Gesloten
          </p>
        </div>
      </section>
    </div>
  );
};
